/**
 * Approval Queue Page
 *
 * Route: /studio/approvals
 *
 * Displays all promotion requests with:
 * - Tab filters by status (All, Pending, Approved, Rejected, Executed)
 * - Environment filter (All, dev->staging, staging->prod)
 * - Table with promotion details
 * - Quick actions for users with config:promote capability
 * - Auto-refresh every 30 seconds
 *
 * Gated to users with config:read capability (everyone can see the queue).
 */

import {
  Loader2,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  Eye,
  CheckCircle,
  XCircle,
  Inbox,
} from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";

import { ApprovalDetailDialog } from "../components/approval/ApprovalDetailDialog";
import {
  createApprovalApiClient,
  useApprovalQueue,
  useApprovalActions,
} from "../hooks/use-approval-api";
import type {
  PromotionResponse,
  PromotionStatus,
  TargetEnvironment,
  ListPromotionsParams,
} from "../hooks/use-approval-api";
import { useAuthStore } from "../store/authStore";

// ============================================================================
// Constants
// ============================================================================

/** Auto-refresh interval: 30 seconds */
const REFRESH_INTERVAL_MS = 30_000;

/** Status tab definitions */
const STATUS_TABS: ReadonlyArray<{ label: string; value: PromotionStatus | "all" }> = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Executed", value: "executed" },
];

/** Environment filter options */
const ENV_FILTERS: ReadonlyArray<{
  label: string;
  value: TargetEnvironment | "all";
}> = [
  { label: "All Environments", value: "all" },
  { label: "dev \u2192 staging", value: "staging" },
  { label: "staging \u2192 prod", value: "prod" },
];

/** Status badge styling */
const STATUS_STYLES: Record<string, string> = {
  pending: "bg-warning/10 text-warning",
  approved: "bg-success/10 text-success",
  rejected: "bg-error/10 text-error",
  executed: "bg-primary/10 text-primary",
  failed: "bg-error/10 text-error",
  cancelled: "bg-foreground-muted/10 text-foreground-muted",
};

// ============================================================================
// Helpers
// ============================================================================

/** Get API base URL from environment */
function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  return envUrl ?? "http://localhost:8787";
}

// ============================================================================
// Main Component
// ============================================================================

export function ApprovalQueuePage(): ReactElement {
  const { hasCapability } = useAuthStore();

  if (!hasCapability("config:read")) {
    return <Navigate to="/studio" replace />;
  }

  return <ApprovalQueueContent />;
}

function ApprovalQueueContent(): ReactElement {
  const { hasCapability, token, user } = useAuthStore();
  const canPromote = hasCapability("config:promote");
  const currentUserId = user?.id ?? "";

  // Filters
  const [statusFilter, setStatusFilter] = useState<PromotionStatus | "all">("all");
  const [envFilter, setEnvFilter] = useState<TargetEnvironment | "all">("all");

  // Selected promotion for detail dialog
  const [selectedPromotion, setSelectedPromotion] = useState<PromotionResponse | null>(null);

  // Build query params
  const filters: ListPromotionsParams = {
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(envFilter !== "all" ? { targetEnvironment: envFilter } : {}),
    limit: 50,
  };

  const clientRef = useRef(
    createApprovalApiClient({
      baseUrl: getApiBaseUrl(),
      getAuthToken: () => useAuthStore.getState().token ?? null,
    })
  );

  const { promotions, isLoading, error, refresh } = useApprovalQueue(clientRef.current, filters);
  const {
    approve,
    reject,
    execute,
    isLoading: isActionLoading,
  } = useApprovalActions(clientRef.current);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!token) return;

    const intervalId = setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [token, refresh]);

  const handleApprove = useCallback(
    async (promotionId: string, notes?: string): Promise<void> => {
      await approve(promotionId, notes);
      await refresh();
      setSelectedPromotion(null);
    },
    [approve, refresh]
  );

  const handleReject = useCallback(
    async (promotionId: string, reason: string): Promise<void> => {
      await reject(promotionId, reason);
      await refresh();
      setSelectedPromotion(null);
    },
    [reject, refresh]
  );

  const handleExecute = useCallback(
    async (promotionId: string): Promise<void> => {
      await execute(promotionId);
      await refresh();
      setSelectedPromotion(null);
    },
    [execute, refresh]
  );

  const handleQuickApprove = useCallback(
    async (promotion: PromotionResponse): Promise<void> => {
      const confirmed = window.confirm(
        `Approve promotion for ${promotion.verticalKey} (${promotion.configTypes.join(", ")}) to ${promotion.targetEnvironment}?`
      );
      if (!confirmed) return;
      await approve(promotion.id);
      await refresh();
    },
    [approve, refresh]
  );

  const handleQuickReject = useCallback((promotion: PromotionResponse): void => {
    setSelectedPromotion(promotion);
  }, []);

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Approval Queue</h1>
          <p className="mt-1 text-foreground-muted">
            Review and manage promotion requests across environments
          </p>
        </div>
        <button onClick={() => void refresh()} className="btn btn-secondary" disabled={isLoading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        {/* Status tabs */}
        <div className="flex gap-1 rounded-lg bg-background-secondary p-1" role="tablist">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              role="tab"
              aria-selected={statusFilter === tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                statusFilter === tab.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-foreground-muted hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Environment filter */}
        <div>
          <select
            value={envFilter}
            onChange={(e) => setEnvFilter(e.target.value as TargetEnvironment | "all")}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Environment filter"
          >
            {ENV_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      {isLoading && promotions.length === 0 ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={() => void refresh()} />
      ) : promotions.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="rounded-lg border border-border bg-background-secondary overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Vertical
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Config Types
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Environment
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Requested By
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {promotions.map((promotion) => (
                <PromotionRow
                  key={promotion.id}
                  promotion={promotion}
                  canPromote={canPromote}
                  currentUserId={currentUserId}
                  onView={() => setSelectedPromotion(promotion)}
                  onQuickApprove={() => void handleQuickApprove(promotion)}
                  onQuickReject={() => handleQuickReject(promotion)}
                  isActionLoading={isActionLoading}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail dialog */}
      {selectedPromotion && (
        <ApprovalDetailDialog
          isOpen={true}
          onClose={() => setSelectedPromotion(null)}
          promotion={selectedPromotion}
          currentUserId={currentUserId}
          canPromote={canPromote}
          onApprove={handleApprove}
          onReject={handleReject}
          onExecute={handleExecute}
          isLoading={isActionLoading}
        />
      )}
    </div>
  );
}

// ============================================================================
// PromotionRow
// ============================================================================

interface PromotionRowProps {
  readonly promotion: PromotionResponse;
  readonly canPromote: boolean;
  readonly currentUserId: string;
  readonly onView: () => void;
  readonly onQuickApprove: () => void;
  readonly onQuickReject: () => void;
  readonly isActionLoading: boolean;
}

function PromotionRow({
  promotion,
  canPromote,
  currentUserId,
  onView,
  onQuickApprove,
  onQuickReject,
  isActionLoading,
}: PromotionRowProps): ReactElement {
  const isPending = promotion.status === "pending";
  const isSelfApproval = promotion.requestedBy === currentUserId;
  const showQuickActions = canPromote && isPending && !isSelfApproval;

  return (
    <tr className="hover:bg-background transition-colors">
      <td className="px-4 py-3 text-sm font-medium">{promotion.verticalKey}</td>
      <td className="px-4 py-3 text-sm text-foreground-muted">
        {promotion.configTypes.join(", ")}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 text-sm">
          <span>{promotion.sourceEnvironment}</span>
          <ArrowRight className="h-3 w-3 text-foreground-muted" />
          <span className="font-medium">{promotion.targetEnvironment}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-foreground-muted">{promotion.requestedBy}</td>
      <td className="px-4 py-3 text-sm text-foreground-muted">
        {new Date(promotion.requestedAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            STATUS_STYLES[promotion.status] ?? STATUS_STYLES["pending"]
          }`}
        >
          {promotion.status.charAt(0).toUpperCase() + promotion.status.slice(1)}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={onView}
            className="rounded-md p-1 text-foreground-muted hover:bg-background hover:text-foreground"
            title="View details"
            data-testid={`view-${promotion.id}`}
          >
            <Eye className="h-4 w-4" />
          </button>
          {showQuickActions && (
            <>
              <button
                onClick={onQuickApprove}
                disabled={isActionLoading}
                className="rounded-md p-1 text-success hover:bg-success/10"
                title="Approve"
                data-testid={`quick-approve-${promotion.id}`}
              >
                <CheckCircle className="h-4 w-4" />
              </button>
              <button
                onClick={onQuickReject}
                disabled={isActionLoading}
                className="rounded-md p-1 text-error hover:bg-error/10"
                title="Reject"
                data-testid={`quick-reject-${promotion.id}`}
              >
                <XCircle className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading promotion requests...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}

function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
        <Inbox className="h-8 w-8 text-foreground-muted" />
      </div>
      <h3 className="mt-4 font-semibold">No promotion requests found</h3>
      <p className="mt-2 text-foreground-muted">
        Promotion requests will appear here when team members request config promotions.
      </p>
    </div>
  );
}
